import { useCallback, useMemo, useState } from 'react'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import { ReactComponent as ChevronDown16 } from 'assets/svg/ChevronDown16.svg'
import { ReactComponent as ChevronUp16 } from 'assets/svg/ChevronUp16.svg'
import { BTN_TXT, LABELS, PLACEHOLDERS } from 'constants/txt'
import { Input } from 'components/Input'
import { Datepicker } from 'components/Datepicker'

import { IContractDetailsForm } from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsForm/types'
import useContractDetailsForm from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsForm/hooks/useContractDetailsForm'
import { numericValueParser } from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import ContractDetailsDocuments from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsDocuments/ContractDetailsDocuments'
import AdditionalInfoPreview from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsAdditionalInfo/AdditionalInfoPreview'
import AdditionalInfoForm from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsAdditionalInfo/AdditionalInfoForm'
import { useContractDetailsContext } from '../../Providers/ContractDetailsContextProvider'
import usePopup from '../../../../hooks/usePopup'
import { Select } from '../../../../components/Select'
import { FloatFooter } from '../../../../components/FloatFooter/FloatFooter'
import { RowLayout } from '../../../MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import { AddContractCategory } from './AddContractCategory/AddContractCategory'
import { Separator } from '../../../../components/Separator/Separator'
import { ResponsibleUsers } from '../ResponsibleUsers'
import { ViewNoteButton } from '../../ViewNote/ViewNoteButton'
import { ACTIONS, contractSubject, SUBJECTS } from '../../Abilities'
import { useContractDetailsAbility } from '../../Providers/ContractDetailsAbilityProvider'
import { CONTRACT_STATUS, VENDOR_FEE_TYPES_VALUES } from '../../constants'
import './styles.scss'
import { LedByInfo } from '../ContractDetailsAdditionalInfo/LedByInfo'
import {
  Typography,
  TYPOGRAPHY_WEIGHT
} from '../../../../components/Typography'
import UserList from '../ResponsibleUsers/UserList/UserList'

const ContractDetailsForm = () => {
  const {
    form,
    isCreation,
    isFormRequired,
    hasFormChanges,
    handleFinishForm,
    handleCancelForm,
    handleChangeForm,
    finishDateFieldStatus,
    disabledDateSameOrBeforeStartDate,
    disabledDateAfterFinishDate,
    isChanged,
    isActive,
    setIsChanged,
    details,
    handleFinishFailedForm,
    canEditStartDate,
    canEditFinishDate
  } = useContractDetailsForm()
  const contractDetailsContext = useContractDetailsContext()
  const ability = useContractDetailsAbility()
  const discardChangesPopup = usePopup()
  const {
    paymentFrequencyOptions,
    paymentTermsOptions,
    vendorFeeTypeOptions,
    shippingMethodOptions,
    isVendor
  } = contractDetailsContext.state
  const [firstLoad, setFirstLoad] = useState(false)

  const adminFee = Form.useWatch('admin_fee', form)
  const start_date = Form.useWatch('start_date', form)
  const finish_date = Form.useWatch('finish_date', form)
  const vendor_fee_type = Form.useWatch('vendor_fee_type', form)

  const VendorFeeType = useMemo(
    () => (
      <Select
        className="full-width"
        options={vendorFeeTypeOptions}
        propsItem={{
          name: 'vendor_fee_type',
          label: LABELS.VENDOR_FEE_TYPE,
          rules: [
            {
              required: true,
              message: 'Required'
            }
          ]
        }}
        propsSelect={{
          placeholder: PLACEHOLDERS.PLEASE_SELECT,
          onChange: handleChangeForm,
          disabled:
            details.status === CONTRACT_STATUS.EXECUTED ||
            details.status === CONTRACT_STATUS.ACTIVE
        }}
      />
    ),
    [vendorFeeTypeOptions, handleChangeForm, details]
  )

  const ShippingMethodField = useMemo(
    () => (
      <Select
        options={shippingMethodOptions}
        propsItem={{
          name: 'shipping_method',
          label: LABELS.SHIPPING_METHOD
        }}
        propsSelect={{
          placeholder: PLACEHOLDERS.PLEASE_SELECT,
          onChange: handleChangeForm
        }}
      />
    ),
    [shippingMethodOptions, handleChangeForm]
  )

  const CustomContractNumberField = useMemo(
    () => (
      <Input
        propsItem={{
          name: 'custom_number',
          label: LABELS.CUSTOM_CONTRACT_NUMBER
        }}
        propsInput={{
          onChange: handleChangeForm,
          maxLength: 20,
          showCount: true
        }}
      />
    ),
    [handleChangeForm]
  )

  const StartDateField = useMemo(
    () => (
      <Datepicker
        propsItem={{
          name: 'start_date',
          label: LABELS.START_DATE,
          rules: [
            {
              required: isFormRequired,
              message: ''
            }
          ],
          help: !start_date && isFormRequired ? ['Provide Start date'] : null,
          validateStatus: !start_date && isFormRequired ? 'error' : undefined
        }}
        propsDate={{
          onChange: handleChangeForm,
          disabledDate: disabledDateAfterFinishDate,
          disabled: !canEditStartDate
        }}
      />
    ),
    [
      handleChangeForm,
      isFormRequired,
      start_date,
      disabledDateAfterFinishDate,
      canEditStartDate
    ]
  )

  const FinishDateField = useMemo(
    () => (
      <Datepicker
        propsItem={{
          name: 'finish_date',
          label: LABELS.FINISH_DATE,
          rules: [
            {
              required: isFormRequired,
              message: ''
            }
          ],
          help: !finish_date && isFormRequired ? ['Provide Finish date'] : null,
          validateStatus: !finish_date && isFormRequired ? 'error' : undefined
        }}
        propsDate={{
          disabledDate: disabledDateSameOrBeforeStartDate,
          status: finishDateFieldStatus,
          onChange: handleChangeForm,
          disabled: !canEditFinishDate
        }}
      />
    ),
    [
      disabledDateSameOrBeforeStartDate,
      finishDateFieldStatus,
      handleChangeForm,
      isFormRequired,
      finish_date,
      canEditFinishDate
    ]
  )

  const AdminFeeField = useMemo(
    () => (
      <Input.Number
        className="spinner-input-number"
        propsItem={{
          name: 'admin_fee',
          label: LABELS.ADMIN_FEE,
          rules: [
            {
              required: true,
              message: 'Required'
            }
          ]
        }}
        propsInputNumber={{
          controls: { downIcon: <ChevronDown16 />, upIcon: <ChevronUp16 /> },
          min: '1',
          max: '3',
          parser: numericValueParser,
          placeholder: '1',
          onChange: handleChangeForm,
          disabled: vendor_fee_type !== VENDOR_FEE_TYPES_VALUES.ADMIN
        }}
      />
    ),
    [handleChangeForm, vendor_fee_type]
  )

  const AdminPaymentFrequencyField = useMemo(
    () => (
      <Select
        options={paymentFrequencyOptions}
        className={
          vendor_fee_type === VENDOR_FEE_TYPES_VALUES.MARKETPLACE_AR
            ? 'full-width'
            : ''
        }
        propsItem={{
          name: 'payment_frequency',
          label:
            vendor_fee_type === VENDOR_FEE_TYPES_VALUES.MARKETPLACE_AR
              ? LABELS.MARKETPLACE_AR_PAYMENT_FREQUENCY
              : LABELS.ADMIN_PAYMENT_FREQUENCY,
          rules: [
            {
              required: true,
              message: 'Required'
            }
          ]
        }}
        propsSelect={{
          placeholder: PLACEHOLDERS.PLEASE_SELECT,
          onChange: handleChangeForm,
          disabled:
            details.status === CONTRACT_STATUS.EXECUTED ||
            details.status === CONTRACT_STATUS.ACTIVE ||
            vendor_fee_type === VENDOR_FEE_TYPES_VALUES.MARKETPLACE_AR
        }}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleChangeForm,
      adminFee,
      paymentFrequencyOptions,
      vendor_fee_type,
      details.status
    ]
  )
  const PaymentTermsField = useMemo(
    () => (
      <Select
        options={paymentTermsOptions}
        propsItem={{
          name: 'payment_term_days',
          label: LABELS.PAYMENT_TERM_DAYS,
          rules: [
            {
              required: true,
              message: 'Required'
            }
          ]
        }}
        propsSelect={{
          placeholder: '0',
          onChange: handleChangeForm,
          disabled:
            details.status === CONTRACT_STATUS.EXECUTED ||
            details.status === CONTRACT_STATUS.ACTIVE
        }}
      />
    ),
    [handleChangeForm, paymentTermsOptions, details.status]
  )

  const submitCancellation = useCallback(() => {
    handleCancelForm()
    discardChangesPopup.actions.close()
    setIsChanged(false)
  }, [discardChangesPopup.actions, handleCancelForm, setIsChanged])

  const FormActions = useMemo(
    () => (
      <FloatFooter
        isActive={isActive}
        onCancel={submitCancellation}
        onSave={form.submit}
        isSaveDisabled={
          !isChanged ||
          details.vendor_fee_type === VENDOR_FEE_TYPES_VALUES.ADMIN
            ? adminFee === null
            : false
        }
        saveBtnText={isCreation ? BTN_TXT.CREATE : BTN_TXT.SAVE}
      />
    ),
    [
      submitCancellation,
      form.submit,
      isActive,
      isChanged,
      isCreation,
      adminFee,
      details.vendor_fee_type
    ]
  )

  return (
    <>
      <CustomForm<IContractDetailsForm>
        id="contract-details-form"
        form={form}
        onValuesChange={handleChangeForm}
        onFinish={handleFinishForm}
        onFinishFailed={handleFinishFailedForm}
        onFieldsChange={() => {
          if (!firstLoad) setFirstLoad(true)
          else setIsChanged(true)
        }}
      >
        <div className="contract-details-form">
          <RowLayout title="Contract Details">
            <div className="column gap-24">
              <div className="contract-details-preview__grid gap-24">
                {StartDateField}
                {FinishDateField}
                {details.uuid && <LedByInfo details={details} />}
                <AddContractCategory form={form} isCreation={isCreation} />
                {ShippingMethodField}
                {CustomContractNumberField}
              </div>
              {details.uuid && (
                <ViewNoteButton
                  title="Contract Note"
                  canViewNote={ability.can(
                    ACTIONS.VIEW,
                    contractSubject(SUBJECTS.NOTE, details)
                  )}
                  canEditNote={ability.can(
                    ACTIONS.EDIT,
                    contractSubject(SUBJECTS.NOTE, details)
                  )}
                  note={details.note}
                />
              )}
            </div>
            {!isCreation && isVendor && (
              <>
                <Typography.Headline6 weight={TYPOGRAPHY_WEIGHT.BOLD}>
                  Community contacts
                </Typography.Headline6>
                <UserList vendor={!isVendor} />
              </>
            )}
          </RowLayout>
          <Separator />
          <RowLayout title="Fees & payment terms">
            <div className="contract-details-form__grid">
              {VendorFeeType}
              {vendor_fee_type !== VENDOR_FEE_TYPES_VALUES.NO_FEE && (
                <>
                  {vendor_fee_type === VENDOR_FEE_TYPES_VALUES.ADMIN &&
                    AdminFeeField}
                  {AdminPaymentFrequencyField}
                </>
              )}
              {PaymentTermsField}
            </div>
          </RowLayout>
          <Separator />
          <RowLayout title="Team Responsible">
            {isCreation ? (
              <AdditionalInfoForm
                form={form}
                handleChangeForm={handleChangeForm}
              />
            ) : (
              <>
                <AdditionalInfoPreview />
                <ResponsibleUsers />
              </>
            )}
          </RowLayout>
          {!isCreation && (
            <>
              <Separator />
              <RowLayout title="Documents">
                <ContractDetailsDocuments />
              </RowLayout>
            </>
          )}
        </div>
      </CustomForm>
      {(hasFormChanges || isCreation) && FormActions}
    </>
  )
}

export default ContractDetailsForm
